import { useState } from 'react';
import type { StatementFilter } from '../useAccountStatement';
import { PAYMENT_METHOD_MODAL_OPTIONS } from '../../../../lib/paymentMethods';

interface StatementFiltersProps {
  filters: StatementFilter;
  onChange: (partial: Partial<StatementFilter>) => void;
  onQuickPeriod: (period: 'thisMonth' | 'lastMonth' | 'last3' | 'last6' | 'thisYear' | 'all') => void;
  totalCount: number;
}

const QUICK_PERIODS = [
  { key: 'thisMonth', label: 'Este mês' },
  { key: 'lastMonth', label: 'Mês anterior' },
  { key: 'last3', label: 'Últ. 3 meses' },
  { key: 'last6', label: 'Últ. 6 meses' },
  { key: 'thisYear', label: 'Este ano' },
  { key: 'all', label: 'Tudo' },
] as const;

const TYPE_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'receita', label: 'Receitas' },
  { value: 'despesa', label: 'Despesas' },
  { value: 'transferencia', label: 'Transf.' },
] as const;

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos status' },
  { value: 'pago', label: 'Pago / Recebido' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'vencido', label: 'Vencido' },
] as const;

export function StatementFilters({ filters, onChange, onQuickPeriod, totalCount }: StatementFiltersProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const activeFiltersCount = [
    filters.type !== 'all',
    filters.status !== 'all',
    filters.search.trim().length > 0,
    filters.paymentMethod !== null,
  ].filter(Boolean).length;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
      {/* Row 1: period tabs + search + advanced toggle */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Quick period — segmented control style */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 overflow-x-auto">
          {QUICK_PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => onQuickPeriod(p.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-all cursor-pointer whitespace-nowrap ${
                p.key === 'thisMonth' && filters.dateFrom === new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
                  ? 'bg-white text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-w-0" />

        {/* Search */}
        <div className="relative min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder="Buscar lançamento..."
            value={filters.search}
            onChange={e => onChange({ search: e.target.value })}
            className="w-full pl-8 pr-8 py-2 border border-gray-200 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-emerald-400 bg-gray-50"
          />
          {filters.search && (
            <button
              onClick={() => onChange({ search: '' })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>

        {/* Advanced toggle */}
        <button
          onClick={() => setShowAdvanced(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-600 border cursor-pointer transition-all whitespace-nowrap ${
            showAdvanced || activeFiltersCount > 0
              ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
              : 'border-gray-200 text-gray-500 hover:border-gray-300'
          }`}
        >
          <i className="ri-filter-3-line text-sm" />
          Filtros
          {activeFiltersCount > 0 && (
            <span className="w-4 h-4 flex items-center justify-center bg-emerald-500 text-white rounded-full text-[10px]">
              {activeFiltersCount}
            </span>
          )}
        </button>
      </div>

      {/* Advanced filters */}
      {showAdvanced && (
        <div className="pt-3 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Date range */}
          <div>
            <p className="text-xs font-600 text-gray-500 mb-2">Período personalizado</p>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={filters.dateFrom}
                onChange={e => onChange({ dateFrom: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-xs text-gray-700 focus:outline-none focus:border-emerald-400 cursor-pointer"
              />
              <span className="text-gray-400 text-xs">até</span>
              <input
                type="date"
                value={filters.dateTo}
                onChange={e => onChange({ dateTo: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-xs text-gray-700 focus:outline-none focus:border-emerald-400 cursor-pointer"
              />
            </div>
          </div>

          {/* Type */}
          <div>
            <p className="text-xs font-600 text-gray-500 mb-2">Tipo</p>
            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
              {TYPE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => onChange({ type: opt.value })}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-600 cursor-pointer transition-all whitespace-nowrap ${
                    filters.type === opt.value
                      ? 'bg-white text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Status */}
          <div>
            <p className="text-xs font-600 text-gray-500 mb-2">Status</p>
            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1 flex-wrap">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => onChange({ status: opt.value })}
                  className={`px-2.5 py-1.5 rounded-lg text-xs font-600 cursor-pointer transition-all whitespace-nowrap ${
                    filters.status === opt.value
                      ? 'bg-white text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Payment method */}
          <div>
            <p className="text-xs font-600 text-gray-500 mb-2">Forma de pagamento</p>
            <select
              value={filters.paymentMethod ?? ''}
              onChange={e => onChange({ paymentMethod: e.target.value || null })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs font-600 text-gray-600 focus:outline-none focus:border-emerald-400 bg-white cursor-pointer"
            >
              <option value="">Qualquer forma</option>
              {PAYMENT_METHOD_MODAL_OPTIONS.map(pm => (
                <option key={pm.value} value={pm.value}>{pm.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Count + clear */}
      <div className="flex items-center justify-between pt-1">
        <p className="text-xs text-gray-400">
          <span className="font-600 text-gray-600">{totalCount}</span> lançamento{totalCount !== 1 ? 's' : ''} no período
        </p>
        {activeFiltersCount > 0 && (
          <button
            onClick={() => onChange({ type: 'all', status: 'all', search: '', paymentMethod: null })}
            className="text-xs text-red-400 hover:text-red-600 cursor-pointer transition-colors flex items-center gap-1"
          >
            <i className="ri-close-circle-line" />
            Limpar filtros
          </button>
        )}
      </div>
    </div>
  );
}
